/**
 * Панель навигации с кнопкой «Назад».
 * Адаптировано из web-client ActionPanel (src/new-layouts/pages/action-panel).
 */

import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { MenuButton } from '../MenuButton';
import { IconChevronLeft } from '../icons';

const Panel = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-grow: 1;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: ${({ theme }) => theme.zIndex.contentSticky};
    max-height: 50px;
    min-height: 50px;
    margin: ${theme.space[2]}px 0 0;
    padding: ${theme.space[0]}px ${theme.space[0]}px ${theme.space[0]}px ${theme.space[1]}px;
    background: ${theme.colors.white};
    border-radius: 8px;
    box-shadow: ${theme.shadows[1]};
  `,
);

const AddonWrapper = styled.div<{ theme: { space: number[] } }>`
  display: flex;
  flex-wrap: nowrap;
  gap: ${({ theme }) => theme.space[0]}px;
  margin-left: auto;
`;

export interface ActionPanelProps {
  /** Путь по умолчанию при отсутствии истории (например /products) */
  defaultPath?: string;
  /** Дополнительные элементы справа (кнопки действий) */
  rightAddon?: ReactNode;
}

function useGoBack(defaultPath = '/') {
  const navigate = useNavigate();

  const goBack = useCallback(
    (fallbackPath?: string) => {
      const path = fallbackPath ?? defaultPath;
      if (typeof window !== 'undefined' && window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(path);
      }
    },
    [navigate, defaultPath],
  );

  return { goBack };
}

export function ActionPanel({ defaultPath = '/products', rightAddon }: ActionPanelProps) {
  const { goBack } = useGoBack(defaultPath);

  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MenuButton
          size={34}
          aria-label="Вернуться назад"
          onClick={() => goBack()}
        >
          <IconChevronLeft />
        </MenuButton>
      </div>
      {rightAddon && <AddonWrapper>{rightAddon}</AddonWrapper>}
    </Panel>
  );
}
